import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    Orientation,
    DEFAULT_DEVICE,
    DEFAULT_ORIENTATION,
    DEVICE_DEFINITIONS,
} from '../../deviceDefinitions';

interface LocalCanvasState {
    zoom: number;
    panX: number;
    panY: number;
    selectedScreenId?: string;
    selectedElementId?: string;
    mode: 'select' | 'pan' | 'connect';
    screenPositions: { [screenId: string]: CanvasPosition };
    connections: NavigationConnection[];
    draggedElementType?: UIElementType;
    isDragFromPalette: boolean;
    isFullscreen: boolean;
    dragHoverScreen?: string;
    dragHoverPosition?: { h: string; v: string };

    // Per-experience device settings (serializable)
    experienceDevices: {
        // Main experience device settings
        main: {
            deviceId: string;
            orientation: Orientation;
        };
        // Sharing station device settings (keyed by sharing station name)
        [sharingStationName: string]: {
            deviceId: string;
            orientation: Orientation;
        };
    };

    // Flow editor state
    isFlowEditorOpen: boolean;

    // Visual preferences
    gridColor: string;

    // Visual preferences state
    preferences: {
        showConnections: boolean;
        showLegend: boolean;
        showTemplates: boolean;
        showHelp: boolean;
    };

    // Active tab state
    activeTab: string; // 'main' or sharing station name
}

const initialState: LocalCanvasState = {
    zoom: 1,
    panX: 0,
    panY: 0,
    selectedScreenId: undefined,
    selectedElementId: undefined,
    mode: 'select',
    screenPositions: {},
    connections: [],
    draggedElementType: undefined,
    isDragFromPalette: false,
    isFullscreen: false,
    dragHoverScreen: undefined,
    dragHoverPosition: undefined,

    // Per-experience device defaults
    experienceDevices: {
        main: {
            deviceId: DEFAULT_DEVICE.id,
            orientation: DEFAULT_ORIENTATION,
        },
    },

    // Flow editor defaults
    isFlowEditorOpen: false,

    // Visual preferences defaults
    gridColor: '#3B82F6', // Default blue color

    // Visual preferences state
    preferences: {
        showConnections: true,
        showLegend: false,
        showTemplates: false,
        showHelp: false,
    },

    // Active tab state
    activeTab: 'main',
};

const canvasSlice = createSlice({
    name: 'canvas',
    initialState,
    reducers: {
        setZoom: (state, action: PayloadAction<number>) => {
            state.zoom = Math.max(0.1, Math.min(3, action.payload));
        },
        setPan: (state, action: PayloadAction<{ x: number; y: number }>) => {
            state.panX = action.payload.x;
            state.panY = action.payload.y;
        },
        setMode: (
            state,
            action: PayloadAction<'select' | 'pan' | 'connect'>,
        ) => {
            state.mode = action.payload;
        },
        selectScreen: (state, action: PayloadAction<string | undefined>) => {
            state.selectedScreenId = action.payload;
            state.selectedElementId = undefined;
        },
        selectElement: (
            state,
            action: PayloadAction<string | null | undefined>,
        ) => {
            state.selectedElementId = action.payload || undefined;
        },
        updateScreenPosition: (
            state,
            action: PayloadAction<{ screenId: string; x: number; y: number }>,
        ) => {
            state.screenPositions[action.payload.screenId] = {
                x: action.payload.x,
                y: action.payload.y,
            };
        },
        addConnection: (state, action: PayloadAction<NavigationConnection>) => {
            state.connections.push(action.payload);
        },
        removeConnection: (state, action: PayloadAction<string>) => {
            state.connections = state.connections.filter(
                (conn) => conn.id !== action.payload,
            );
        },
        setDraggedElementType: (
            state,
            action: PayloadAction<UIElementType | undefined>,
        ) => {
            state.draggedElementType = action.payload;
        },
        startDragFromPalette: (state, action: PayloadAction<UIElementType>) => {
            state.isDragFromPalette = true;
            state.draggedElementType = action.payload;
        },
        endDragFromPalette: (state) => {
            state.isDragFromPalette = false;
            state.draggedElementType = undefined;
            state.dragHoverScreen = undefined;
            state.dragHoverPosition = undefined;
        },
        setDragHover: (
            state,
            action: PayloadAction<{
                screenId?: string;
                position?: { h: string; v: string };
            }>,
        ) => {
            state.dragHoverScreen = action.payload.screenId;
            state.dragHoverPosition = action.payload.position;
        },
        resetCanvas: (state) => {
            Object.assign(state, initialState);
        },
        toggleFullscreen: (state) => {
            state.isFullscreen = !state.isFullscreen;
        },
        setFullscreen: (state, action: PayloadAction<boolean>) => {
            state.isFullscreen = action.payload;
        },
        initializeScreenPositions: (
            state,
            action: PayloadAction<ScreenDefinition[]>,
        ) => {
            const SCREEN_WIDTH = 280;
            const SCREEN_HEIGHT = 400;
            const GRID_SPACING = 60; // Increased spacing to prevent overlaps
            const START_X = 50; // Upper-left starting position
            const START_Y = 50;

            action.payload.forEach((screen, index) => {
                if (!state.screenPositions[screen.id]) {
                    if (index === 0) {
                        // Position first screen in upper-left as entry point
                        state.screenPositions[screen.id] = {
                            x: START_X,
                            y: START_Y,
                        };
                    } else {
                        // Use a non-overlapping grid layout for subsequent screens
                        // Calculate grid position (3 columns for better spacing)
                        const col = (index - 1) % 3;
                        const row = Math.floor((index - 1) / 3);

                        const x =
                            START_X + (col + 1) * (SCREEN_WIDTH + GRID_SPACING);
                        const y =
                            START_Y + row * (SCREEN_HEIGHT + GRID_SPACING);

                        state.screenPositions[screen.id] = { x, y };
                    }
                }
            });
        },
        addScreenAtCenter: (
            state,
            action: PayloadAction<{
                screenId: string;
                canvasWidth?: number;
                canvasHeight?: number;
            }>,
        ) => {
            const {
                screenId,
                canvasWidth = 1200,
                canvasHeight = 800,
            } = action.payload;

            // Calculate center position accounting for screen size
            const SCREEN_WIDTH = 280;
            const SCREEN_HEIGHT = 400;
            const centerX = (canvasWidth - SCREEN_WIDTH) / 2;
            const centerY = (canvasHeight - SCREEN_HEIGHT) / 2;

            state.screenPositions[screenId] = {
                x: centerX,
                y: centerY,
            };
        },
        // Per-experience device settings
        setExperienceDevice: (
            state,
            action: PayloadAction<{
                experienceName: string; // 'main' or sharing station name
                deviceId: string;
                orientation: Orientation;
            }>,
        ) => {
            const { experienceName, deviceId, orientation } = action.payload;
            state.experienceDevices[experienceName] = { deviceId, orientation };
        },
        setExperienceDeviceId: (
            state,
            action: PayloadAction<{ experienceName: string; deviceId: string }>,
        ) => {
            const { experienceName, deviceId } = action.payload;
            if (!state.experienceDevices[experienceName]) {
                state.experienceDevices[experienceName] = {
                    deviceId,
                    orientation: DEFAULT_ORIENTATION,
                };
            } else {
                state.experienceDevices[experienceName].deviceId = deviceId;
            }
        },
        setExperienceOrientation: (
            state,
            action: PayloadAction<{
                experienceName: string;
                orientation: Orientation;
            }>,
        ) => {
            const { experienceName, orientation } = action.payload;
            if (!state.experienceDevices[experienceName]) {
                state.experienceDevices[experienceName] = {
                    deviceId: DEFAULT_DEVICE.id,
                    orientation,
                };
            } else {
                state.experienceDevices[experienceName].orientation =
                    orientation;
            }
        },
        toggleExperienceOrientation: (state, action: PayloadAction<string>) => {
            const experienceName = action.payload;
            if (!state.experienceDevices[experienceName]) {
                state.experienceDevices[experienceName] = {
                    deviceId: DEFAULT_DEVICE.id,
                    orientation:
                        DEFAULT_ORIENTATION === 'portrait'
                            ? 'landscape'
                            : 'portrait',
                };
            } else {
                state.experienceDevices[experienceName].orientation =
                    state.experienceDevices[experienceName].orientation ===
                    'portrait'
                        ? 'landscape'
                        : 'portrait';
            }
        },
        removeExperienceDevice: (state, action: PayloadAction<string>) => {
            const experienceName = action.payload;
            if (experienceName !== 'main') {
                delete state.experienceDevices[experienceName];
            }
        },
        // Helper to create sharing station with inherited device settings
        createSharingStationDevice: (
            state,
            action: PayloadAction<string>, // sharing station name
        ) => {
            const sharingStationName = action.payload;
            // Inherit device settings from main experience
            state.experienceDevices[sharingStationName] = {
                ...state.experienceDevices.main,
            };
        },

        // Visual preferences
        setPreference: (
            state,
            action: PayloadAction<{
                key: keyof LocalCanvasState['preferences'];
                value: boolean;
            }>,
        ) => {
            const { key, value } = action.payload;
            state.preferences[key] = value;
        },
        setPreferences: (
            state,
            action: PayloadAction<Partial<LocalCanvasState['preferences']>>,
        ) => {
            state.preferences = { ...state.preferences, ...action.payload };
        },

        // Active tab
        setActiveTab: (state, action: PayloadAction<string>) => {
            state.activeTab = action.payload;
        },

        // Other settings
        setFlowEditorOpen: (state, action: PayloadAction<boolean>) => {
            state.isFlowEditorOpen = action.payload;
        },
        setGridColor: (state, action: PayloadAction<string>) => {
            state.gridColor = action.payload;
        },

        // Serializable state management
        updateCanvasScreenId: (
            state,
            action: PayloadAction<{ oldId: string; newId: string }>,
        ) => {
            const { oldId, newId } = action.payload;

            // Update screen positions
            if (state.screenPositions[oldId]) {
                state.screenPositions[newId] = state.screenPositions[oldId];
                delete state.screenPositions[oldId];
            }

            // Update connections that reference this screen
            state.connections = state.connections.map((conn) => ({
                ...conn,
                fromScreenId:
                    conn.fromScreenId === oldId ? newId : conn.fromScreenId,
                toScreenId: conn.toScreenId === oldId ? newId : conn.toScreenId,
            }));

            // Update selected screen ID if it matches the old ID
            if (state.selectedScreenId === oldId) {
                state.selectedScreenId = newId;
            }

            // Update drag hover screen if it matches
            if (state.dragHoverScreen === oldId) {
                state.dragHoverScreen = newId;
            }
        },
        loadSerializableState: (
            state,
            action: PayloadAction<Partial<SerializableUIState>>,
        ) => {
            const uiState = action.payload;

            if (uiState.canvas) {
                state.zoom = uiState.canvas.zoom ?? state.zoom;
                state.panX = uiState.canvas.panX ?? state.panX;
                state.panY = uiState.canvas.panY ?? state.panY;
                state.selectedScreenId = uiState.canvas.selectedScreenId;
                state.selectedElementId = uiState.canvas.selectedElementId;
                state.mode = uiState.canvas.mode ?? state.mode;
                state.screenPositions =
                    uiState.canvas.screenPositions ?? state.screenPositions;
                state.isFullscreen =
                    uiState.canvas.isFullscreen ?? state.isFullscreen;
                state.gridColor = uiState.canvas.gridColor ?? state.gridColor;
            }

            if (uiState.experienceDevices) {
                state.experienceDevices = uiState.experienceDevices;
            }

            if (uiState.preferences) {
                state.preferences = {
                    ...state.preferences,
                    ...uiState.preferences,
                };
            }

            if (uiState.activeTab) {
                state.activeTab = uiState.activeTab;
            }
        },
    },
});

export const {
    setZoom,
    setPan,
    setMode,
    selectScreen,
    selectElement,
    updateScreenPosition,
    addConnection,
    removeConnection,
    setDraggedElementType,
    startDragFromPalette,
    endDragFromPalette,
    setDragHover,
    resetCanvas,
    toggleFullscreen,
    setFullscreen,
    initializeScreenPositions,
    addScreenAtCenter,
    // Per-experience device settings
    setExperienceDevice,
    setExperienceDeviceId,
    setExperienceOrientation,
    toggleExperienceOrientation,
    removeExperienceDevice,
    createSharingStationDevice,
    // Visual preferences
    setPreference,
    setPreferences,
    // Active tab
    setActiveTab,
    // Other settings
    setFlowEditorOpen,
    setGridColor,
    // Screen ID updates
    updateCanvasScreenId,
    // Serializable state management
    loadSerializableState,
} = canvasSlice.actions;

export default canvasSlice.reducer;

// Helper functions for getting device information
export const getExperienceDevice = (
    state: LocalCanvasState,
    experienceName: string,
) => {
    return (
        state.experienceDevices[experienceName] || state.experienceDevices.main
    );
};

export const getExperienceDeviceDefinition = (
    state: LocalCanvasState,
    experienceName: string,
) => {
    const deviceConfig = getExperienceDevice(state, experienceName);
    const device =
        DEVICE_DEFINITIONS.find((d) => d.id === deviceConfig.deviceId) ||
        DEFAULT_DEVICE;
    return { device, orientation: deviceConfig.orientation };
};

export const getCurrentExperienceDevice = (state: LocalCanvasState) => {
    return getExperienceDevice(state, state.activeTab);
};

export const getCurrentExperienceDeviceDefinition = (
    state: LocalCanvasState,
) => {
    return getExperienceDeviceDefinition(state, state.activeTab);
};

// Helper function to export serializable state
export const getSerializableState = (
    state: LocalCanvasState,
): SerializableUIState => {
    return {
        canvas: {
            zoom: state.zoom,
            panX: state.panX,
            panY: state.panY,
            selectedScreenId: state.selectedScreenId,
            selectedElementId: state.selectedElementId,
            mode: state.mode,
            screenPositions: state.screenPositions,
            isFullscreen: state.isFullscreen,
            gridColor: state.gridColor,
        },
        experienceDevices: state.experienceDevices,
        preferences: state.preferences,
        activeTab: state.activeTab,
    };
};
